import {
  abortElaboration,
  checkboxEdit,
  checkboxInsert,
  coObjectDelete,
  documentPartInsert,
  documentPartLink,
  documentPartNonTagetik,
  dropdownEdit,
  dropdownInsert,
  editTgkAmount,
  editTgkAmountAih,
  editTgkDimensionAttribute,
  editTgkTable,
  editTgkText,
  freeTextEdit,
  freeTextInsert,
  startElaborationForTgkAmountAihInsert,
  startElaborationForTgkAmountInsert,
  startElaborationForTgkDimensionAttributeInsert,
  startElaborationForTgkTableInsert,
  startElaborationForTgkTextInsert,
} from "../../../api/object/CoObjectGeneral";
import { addTitleAndTag, deleteContentControlById, editTitleAndTag } from "../../../utils/helpers/ContentControlHelper";
import { insertCheckbox } from "../../../office/WordCheckbox";
import { insertDropDownList, updateDropDownList } from "../../../office/WordDropDownList";
import { insertFreeText } from "../../../office/WordFreeText";
import {
  CoObjectDocumentPartInclude,
  DocumentPart,
  ElaborationAbortRequest,
  TgkAmountElaborationResponse,
  TgkDimensionAttributeElaborationResponse,
  TgkTableElaborationResponse,
  TgkTextElaborationResponse,
} from "../../../api/models";
import { createContentControlForCoObject, lockObjectCannotEdit } from "../../../office/ContentControl";
import { ContentControlCreationError, InsertionLocationNotAllowedError } from "../../../office/ContentControlErrors";
import {
  CREATE_DOCUMENT_PART_NOTIFICATION,
  DELETE_OBJECT_NOTIFICATION,
  DELETE_OBJECTS_NOTIFICATION,
  EDIT_OBJECT_NOTIFICATION,
  INSERT_OBJECT_NOTIFICATION,
} from "../../../utils/constants/notification";
import {
  CoObjectItem,
  DocumentPartSource,
  TgkAmountObjectSource,
  TgkTextSource,
} from "../../../components/interfaces/CoObject";
import {
  DocumentInsertionPoint,
  insertDocumentPart,
  moveCursorToContentControl,
} from "../../../office/WordDocumentPart";
import { normalizeDocumentName } from "../../../utils/helpers/ObjectCheckerHelper";
import NotificationManager from "../../../common/notification/NotificationManager";
import { formatMessage, formatMessageDefault, formatMessageWithParams } from "../../../utils/helpers/LanguageHelper";
import { TableUpdateStrategy } from "../../../components/coobjects/co-panel/ObjectPanelComponent";
import { navigate } from "../general/navigation-slice";
import { EDIT_OBJECT } from "../../../utils/constants/navigation";
import { store } from "../../store/store";
import { invalidate } from "../validation/validation-slice";
import {
  mapCheckboxConfigToCheckbox,
  mapCoObjectToTgkAmountAihElaborationRequest,
  mapCoObjectToTgkAmountElaborationRequest,
  mapCoObjectToTgkDimensionAttributeElaborationRequest,
  mapCoObjectToTgkTableElaborationRequest,
  mapCoObjectToTgkTextElaborationRequest,
  mapDocumentPartConfigToDocumentPart,
  mapDropDownConfigToDropDown,
  mapFreeTextConfigToFreeText,
} from "./map-objects";
import { isComponentInstance } from "../../../utils/helpers/ComponentInstanceHelper";
import { ElaborationMessage } from "../object/remote-elaborations-slice";
import { DeleteCoObjectsResult } from "../../../api/models/delete-co-objects-result";
import { DimensionParameter } from "../../../api/models/dimension-parameter";

interface InputType {
  documentPart: DocumentPart;
  coObject: CoObjectItem;
  userId: string;
  inheritableParameters?: DimensionParameter[];
  language?: string;
  multilinked?: boolean;
  documentInsertionPoint?: DocumentInsertionPoint;
  linkDetail?: { partDocumentId: string } | { documentDriveItemId: string };
  tableUpdateType?: TableUpdateStrategy["typeUpdate"];
}

export const isObjectWithElaboration = (objectType: string) => {
  return (
    objectType == "TABLE" ||
    objectType == "CHART" ||
    objectType == "AMOUNT" ||
    objectType == "AMOUNT_AIH" ||
    objectType == "DIMENSION_ATTRIBUTE" ||
    objectType == "TGK_TEXT" ||
    objectType == "DOCUMENT_PART_INCLUDE"
  );
};

export const addNotification = (
  key: string,
  title: string,
  message: string,
  options: {
    type?: "success" | "error" | "warning" | "info";
    closable?: boolean;
    removeOnClose?: boolean;
    isTemporary?: boolean;
    enableReloadPanel?: boolean;
  } = {}
) => {
  const {
    type = "success",
    closable = true,
    removeOnClose = true,
    isTemporary = true,
    enableReloadPanel = false,
  } = options;

  NotificationManager.addNotification({
    key: key,
    message: message,
    title: title,
    type: type,
    closable: closable,
    removeOnClose: removeOnClose,
    isTemporary: isTemporary,
    enableReloadPanel: enableReloadPanel,
  });
};

/** Duck-typed shape of an Office.js RichApi/OfficeExtension error's debug payload. */
interface OfficeDebugInfo {
  errorLocation?: string;
  statement?: string;
  surroundingStatements?: string[];
}

/** Best-effort extraction of the Office.js diagnostic payload from a caught value. */
function extractOfficeErrorDetails(error: unknown): { code?: string; debugInfo?: OfficeDebugInfo } | undefined {
  // Check this first: ContentControlCreationError flattens code/errorLocation onto itself (so it
  // would also match the generic duck-type check below), but the richer debugInfo (statement,
  // surroundingStatements) only lives on the original Office.js cause, which must be unwrapped.
  if (error instanceof ContentControlCreationError) {
    const cause = error.cause;
    if (cause && typeof cause === "object" && "code" in cause) {
      const withCode = cause as { code?: string; debugInfo?: OfficeDebugInfo };
      return { code: withCode.code ?? error.code, debugInfo: withCode.debugInfo };
    }
    if (error.code || error.errorLocation) {
      return { code: error.code, debugInfo: { errorLocation: error.errorLocation } };
    }
    return undefined;
  }
  if (error && typeof error === "object" && "code" in error) {
    const withCode = error as { code?: string; debugInfo?: OfficeDebugInfo };
    return { code: withCode.code, debugInfo: withCode.debugInfo };
  }
  return undefined;
}

/** Logs an insert failure, serializing the Office.js diagnostic payload when present. */
function logInsertFailure(error: unknown): void {
  const details = extractOfficeErrorDetails(error);
  if (!details) {
    console.warn(error);
    return;
  }
  console.warn("createContentControlAndLaunchElaboration failed", {
    message: error instanceof Error ? error.message : String(error),
    code: details.code,
    errorLocation: details.debugInfo?.errorLocation,
    statement: details.debugInfo?.statement,
    surroundingStatements: details.debugInfo?.surroundingStatements,
  });
}

export const createContentControlAndLaunchElaboration = async (input: InputType) => {
  let itemResult = null; // TODO should be CoObjectItem but there is an error on CoObjectSource model (CreateDocumentPartModal:93)
  let insertElaborationEntered = false;
  try {
    const {
      userId,
      tableUpdateType,
      documentPart,
      coObject,
      documentInsertionPoint,
      linkDetail,
      language,
      inheritableParameters,
    } = input;
    const isDocPart = coObject.info.objectType === "DOCUMENT_PART_INCLUDE";

    if (isDocPart) {
      itemResult = await createOrLinkDocPart(
        coObject,
        inheritableParameters,
        documentInsertionPoint,
        language,
        linkDetail
      );
    } else {
      const contentControl = await createCCByType(coObject);
      insertElaborationEntered = true;
      itemResult = await insertElaboration(
        contentControl.id,
        documentPart,
        coObject,
        language,
        userId,
        tableUpdateType
      );
      try {
        await moveCursorToContentControl(contentControl.id);
      } catch (cursorError) {
        // Non-fatal: the object was inserted (and possibly elaborated) successfully, so a
        // cursor-move failure must never turn a successful insert into a failed one.
        console.warn("Failed to move cursor to the newly inserted content control", cursorError);
      }
      setTimeout(() => {
        store.dispatch(navigate(EDIT_OBJECT));
      }, 1000);
    }

    if (!isObjectWithElaboration(input.coObject.info.objectType)) {
      addNotification(
        INSERT_OBJECT_NOTIFICATION,
        formatMessage("cof.createnewobject.message.addsuccess.title"),
        formatMessage("cof.createnewobject.message.addsuccess")
      );
    }
    return itemResult;
  } catch (e) {
    logInsertFailure(e);

    // Only clean up an orphan content control when creation failed before insertElaboration was
    // even called: after that point a backend elaboration may already exist, and deleting the
    // content control would orphan it instead (strictly worse than leaving a stray content control).
    if (!insertElaborationEntered && e instanceof ContentControlCreationError) {
      if (e.contentControlId === undefined) {
        // Creation failed before an id was ever assigned: nothing to delete here, rely on the
        // in-context best-effort recovery already attempted inside createContentControlForCoObject.
        console.warn("Content control creation failed with no known id; nothing to clean up here.");
      } else if (!e.orphanCleaned) {
        await deleteContentControlById(e.contentControlId);
      }
    }

    const message =
      e instanceof InsertionLocationNotAllowedError
        ? formatMessage("cof.createnewobject.save.message.error.innerobject.notpermitted")
        : formatMessageDefault(
            "cof.createnewobject.message.addfailed",
            "The object could not be added to the document. Please try again."
          );
    addNotification(INSERT_OBJECT_NOTIFICATION, "", message, { type: "error" });

    throw e;
  }
};

export async function createCCByType(coObject: CoObjectItem): Promise<Word.ContentControl> {
  let cc: Word.ContentControl;
  switch (coObject.info.objectType) {
    case "TGK_TEXT":
    case "DIMENSION_ATTRIBUTE":
    case "AMOUNT":
    case "AMOUNT_AIH": {
      cc = await createContentControlForCoObject(coObject, true);
      break;
    }
    case "TABLE":
    case "CHART": {
      cc = await createContentControlForCoObject(coObject, false);
      break;
    }
    case "CHECKBOX": {
      cc = await insertCheckbox(coObject);
      break;
    }
    case "DROPDOWN_LIST": {
      cc = await insertDropDownList(coObject);
      break;
    }
    case "FREE_TEXT": {
      cc = await insertFreeText(coObject);
      break;
    }
    default:
      return null;
  }

  coObject.contentControlId = cc?.id;
  return cc;
}

export async function insertElaboration(
  contentControlId: number,
  documentPart: DocumentPart,
  coObject: CoObjectItem,
  language: string,
  userId: string,
  tableUpdateType?: TableUpdateStrategy["typeUpdate"]
): Promise<CoObjectItem | null> {
  switch (coObject.info.objectType) {
    case "TABLE":
    case "CHART":
      return await insertTgkTable(contentControlId, documentPart, coObject, language, userId, tableUpdateType);
    case "AMOUNT":
      return coObject.source?.type === "AMOUNT_AIH"
        ? await insertTgkAIHAmount(contentControlId, coObject, userId)
        : await insertTgkAmount(contentControlId, coObject, language, userId);
    case "DIMENSION_ATTRIBUTE":
      return await insertTgkDimensionAttribute(contentControlId, coObject, language, userId);
    case "CHECKBOX":
      return await insertTgkCheckBox(contentControlId, coObject);
    case "DROPDOWN_LIST":
      return await insertTgkDropDownList(contentControlId, coObject);
    case "FREE_TEXT":
      return await insertTgkFreeText(contentControlId, coObject);
    case "TGK_TEXT":
      return await insertTgkText(contentControlId, coObject, userId);
  }
}

export const abortRefreshAsync = async (elaborationRequest: ElaborationAbortRequest) => {
  try {
    await abortElaboration(elaborationRequest);
  } catch (e) {
    NotificationManager.addNotification({
      key: EDIT_OBJECT_NOTIFICATION,
      message: formatMessageDefault("cof.elaboration.abort.message.error", "Error on elaboration abort: ") + e.message,
      title: formatMessageDefault(`cof.elaboration.abort.title`, "Elaboration abort"),
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
    console.error(e.message, e);
  }
};

async function launchEditElaborationAndUpdateContentControl(
  contentControlId: number,
  documentPart: DocumentPart,
  coObject: CoObjectItem,
  language: string,
  userId: string,
  isChanged: boolean,
  tableUpdateType: TableUpdateStrategy["typeUpdate"]
) {
  const isSimpleEdit = !isChanged || isComponentInstance(coObject);
  let tgkTableElaborationResponse: TgkTableElaborationResponse;
  let tgkAmountElaborationResponse: TgkAmountElaborationResponse;
  let tgkDimensionAttributeElaborationResponse: TgkDimensionAttributeElaborationResponse;
  let tgkTextElaborationResponse: TgkTextElaborationResponse;

  switch (coObject.info.objectType) {
    case "TABLE":
    case "CHART":
      tgkTableElaborationResponse = await editTgkTable(
        mapCoObjectToTgkTableElaborationRequest(documentPart, coObject, language, userId, tableUpdateType),
        isSimpleEdit
      );
      coObject.id = tgkTableElaborationResponse.id;
      break;
    case "AMOUNT":
      tgkAmountElaborationResponse =
        coObject.source.type === "AMOUNT_AIH"
          ? await editTgkAmountAih(mapCoObjectToTgkAmountAihElaborationRequest(coObject, userId), isSimpleEdit)
          : await editTgkAmount(mapCoObjectToTgkAmountElaborationRequest(coObject, language, userId), isSimpleEdit);
      coObject.id = tgkAmountElaborationResponse.id;
      coObject.source = {
        ...coObject.source,
        process: tgkAmountElaborationResponse.process,
      } as TgkAmountObjectSource;

      break;
    case "DIMENSION_ATTRIBUTE":
      tgkDimensionAttributeElaborationResponse = await editTgkDimensionAttribute(
        mapCoObjectToTgkDimensionAttributeElaborationRequest(coObject, language, userId),
        isSimpleEdit
      );
      coObject.id = tgkDimensionAttributeElaborationResponse.id;
      break;
    case "CHECKBOX":
      coObject.id = await checkboxEdit(mapCheckboxConfigToCheckbox(coObject));
      break;
    case "DROPDOWN_LIST":
      coObject.id = await dropdownEdit(mapDropDownConfigToDropDown(coObject));

      if (isChanged) {
        await updateDropDownList(contentControlId, coObject);
      }
      break;
    case "FREE_TEXT":
      coObject.id = await freeTextEdit(mapFreeTextConfigToFreeText(coObject));

      break;
    case "TGK_TEXT":
      tgkTextElaborationResponse = await editTgkText(
        mapCoObjectToTgkTextElaborationRequest(coObject, userId),
        isSimpleEdit
      );
      coObject.id = tgkTextElaborationResponse.id;
      coObject.source = {
        ...coObject.source,
        process: tgkTextElaborationResponse.process,
      } as TgkTextSource;

      break;
  }
  editTitleAndTag(coObject);
}

export const editContentControlAndLaunchElaboration = async (input: {
  documentPart: DocumentPart;
  coObject: CoObjectItem;
  language: string;
  userId: string;
  isChanged: boolean;
  tableUpdateType: TableUpdateStrategy["typeUpdate"];
}) => {
  const { documentPart, coObject, language, userId, isChanged, tableUpdateType } = input;
  try {
    await launchEditElaborationAndUpdateContentControl(
      Number(coObject.contentControlId),
      documentPart,
      coObject,
      language,
      userId,
      isChanged,
      tableUpdateType
    );

    store.dispatch(invalidate());

    if (!isObjectWithElaboration(input.coObject.info.objectType) || !input.isChanged) {
      addNotification(
        EDIT_OBJECT_NOTIFICATION,
        formatMessage("cof.createnewobject.message.editsuccess.title"),
        formatMessage("cof.createnewobject.message.editsuccess")
      );
    }
  } catch (e) {
    console.error(e.message, e);
    addNotification(
      EDIT_OBJECT_NOTIFICATION,
      formatMessage("cof.createnewobject.message.editfailed.title"),
      formatMessage("cof.createnewobject.message.editfailed") + e.message,
      {
        type: "error",
        isTemporary: false,
        enableReloadPanel: e.enableRefreshPanelOption,
      }
    );
  }
  return input.coObject.id;
};

export const deleteObjectsAsync = async (input: { coObjects: CoObjectItem[] | CoObjectItem }) => {
  if (!Array.isArray(input.coObjects)) {
    input.coObjects = [input.coObjects];
  }
  if (!input.coObjects.length) return;
  if (input.coObjects.length === 1) {
    return deleteObjectAsync({ coObject: input.coObjects[0] });
  }
  let result: DeleteCoObjectsResult[];
  try {
    result = (await coObjectDelete(input.coObjects.map((o) => o.id))) as {
      id: string;
      success: boolean;
    }[];
  } catch (e) {
    NotificationManager.addNotification({
      key: DELETE_OBJECTS_NOTIFICATION,
      message: formatMessageWithParams("cof.elaboration.deleteobjects.message.deletefail", {
        objectsCount: input.coObjects.length,
        exception: e.message,
      }),
      title: formatMessage("cof.base.error"),
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
      enableReloadPanel: e.enableRefreshPanelOption,
    });
    return Promise.reject(e instanceof Error ? e : new Error(String(e)));
  }

  let failed: ElaborationMessage[] = [];
  for (const coObject of input.coObjects) {
    if (result.find((r: DeleteCoObjectsResult) => r.id === coObject.id && r.success)) {
      await deleteContentControlById(coObject.contentControlId);
    } else {
      failed.push({
        text: formatMessageWithParams("cof.elaboration.deleteobject.message.notdelete", {
          coObjectName: coObject.info.objectName,
          exception: "Server error",
        }),
        details: "",
        severity: "error",
        traceId: "NOT DEFINED",
      });
    }
  }

  if (failed.length > 0) {
    NotificationManager.addNotification({
      key: DELETE_OBJECTS_NOTIFICATION,
      message: formatMessageWithParams("cof.elaboration.deleteobjects.message.deletepartialfail", {
        failedCount: failed.length,
        succeededCount: input.coObjects.length - failed.length,
      }),
      title: formatMessage("cof.elaboration.deleteobjects.message.deletepartialfail.title"),
      details: failed,
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
    return Promise.reject(new Error("Some objects have not been deleted"));
  }

  NotificationManager.addNotification({
    key: DELETE_OBJECTS_NOTIFICATION,
    message: formatMessageWithParams("cof.elaboration.deleteobjects.message.deletesuccess", {
      count: input.coObjects.length,
    }),
    title: formatMessage("cof.elaboration.deleteobjects.message.deletesuccess.title"),
    type: "success",
    closable: true,
    removeOnClose: true,
    isTemporary: true,
  });
};

export const deleteObjectAsync = async (input: { coObject: CoObjectItem }) => {
  let result: boolean;
  try {
    result = (await coObjectDelete(input.coObject.id)) as boolean;
  } catch (e: any) {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessageWithParams("cof.elaboration.deleteobject.message.notdelete", {
        coObjectName: input.coObject.info.objectName,
        exception: e.message,
      }),
      title: formatMessage("cof.createnewobject.message.deletefailed.title"),
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
      enableReloadPanel: e.enableRefreshPanelOption,
    });
    return Promise.reject(e instanceof Error ? e : new Error(String(e)));
  }

  if (!result) {
    const error = new Error("Server error on coObject delete");
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessageWithParams("cof.elaboration.deleteobject.message.notdelete", {
        coObjectName: input.coObject.info.objectName,
        exception: error.message,
      }),
      title: formatMessage("cof.createnewobject.message.deletefailed.title"),
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
    return Promise.reject(error);
  }

  await deleteContentControlById(input.coObject.contentControlId);

  NotificationManager.addNotification({
    key: DELETE_OBJECT_NOTIFICATION,
    message: formatMessageWithParams("cof.createnewobject.message.deletesuccess", {
      coObjectName: input.coObject.info.objectName,
    }),
    title: formatMessage("cof.createnewobject.message.deletesuccess.title"),
    type: "success",
    closable: true,
    removeOnClose: true,
    isTemporary: true,
  });
};

export async function lockObject(coObject: CoObjectItem, lock: boolean = true) {
  coObject = { ...coObject, isLocked: lock };
  await lockObjectCannotEdit(Number(coObject.contentControlId), lock);
  editTitleAndTag(coObject);
}

// Awaits the addTitleAndTag write and guards the insert flow against its failure. At every
// insertTgk* call site below, coObject.id has already been assigned from the backend elaboration
// (or insert) response by the time this runs, so letting the failure propagate would trip the
// orphan-cleanup path in createContentControlAndLaunchElaboration's catch block and delete a
// content control whose backend elaboration already exists -- orphaning the backend object, which
// is strictly worse than a missing title/tag. So we log and warn the user instead of rethrowing.
async function awaitAndGuardTitleAndTag(contentControlId: number, coObject: CoObjectItem): Promise<void> {
  try {
    await addTitleAndTag(contentControlId, coObject);
  } catch (e) {
    console.error(`addTitleAndTag failed for content control ${contentControlId}`, e);
    addNotification(
      INSERT_OBJECT_NOTIFICATION,
      formatMessageDefault("cof.createnewobject.message.tagwritefailed.title", "Object created"),
      formatMessageDefault(
        "cof.createnewobject.message.tagwritefailed",
        "The object was added to the document, but its title and tag could not be saved. Please try again."
      ),
      { type: "warning" }
    );
  }
}

async function insertTgkTable(
  contentControlId: number,
  documentPart: DocumentPart,
  coObject: CoObjectItem,
  language: string,
  userId: string,
  tableUpdateType: TableUpdateStrategy["typeUpdate"]
) {
  let tgkTableElaborationResponse: TgkTableElaborationResponse;
  tgkTableElaborationResponse = await startElaborationForTgkTableInsert(
    mapCoObjectToTgkTableElaborationRequest(documentPart, coObject, language, userId, tableUpdateType)
  );
  coObject.id = tgkTableElaborationResponse.id;
  await awaitAndGuardTitleAndTag(contentControlId, coObject);
  return coObject;
}

async function insertTgkAmount(contentControlId: number, coObject: CoObjectItem, language: string, userId: string) {
  const tgkAmountElaborationResponse = await startElaborationForTgkAmountInsert(
    mapCoObjectToTgkAmountElaborationRequest(coObject, language, userId)
  );

  coObject.id = tgkAmountElaborationResponse.id;
  await awaitAndGuardTitleAndTag(contentControlId, coObject);
  return coObject;
}

async function insertTgkAIHAmount(contentControlId: number, coObject: CoObjectItem, userId: string) {
  const tgkAmountElaborationResponse = await startElaborationForTgkAmountAihInsert(
    mapCoObjectToTgkAmountAihElaborationRequest(coObject, userId)
  );
  coObject.source = {
    ...coObject.source,
    process: tgkAmountElaborationResponse.process,
  } as TgkTextSource;
  coObject.id = tgkAmountElaborationResponse.id;
  await awaitAndGuardTitleAndTag(contentControlId, coObject);
  return coObject;
}

async function insertTgkDimensionAttribute(
  contentControlId: number,
  coObject: CoObjectItem,
  language: string,
  userId: string
) {
  const tgkDimensionAttributeElaborationResponse = await startElaborationForTgkDimensionAttributeInsert(
    mapCoObjectToTgkDimensionAttributeElaborationRequest(coObject, language, userId)
  );

  coObject.id = tgkDimensionAttributeElaborationResponse.id;
  await awaitAndGuardTitleAndTag(contentControlId, coObject);
  return coObject;
}

async function insertTgkCheckBox(contentControlId: number, coObject: CoObjectItem) {
  coObject.id = await checkboxInsert(mapCheckboxConfigToCheckbox(coObject));
  await awaitAndGuardTitleAndTag(contentControlId, coObject);
  return coObject;
}

async function insertTgkDropDownList(contentControlId: number, coObject: CoObjectItem) {
  coObject.id = await dropdownInsert(mapDropDownConfigToDropDown(coObject));
  await awaitAndGuardTitleAndTag(contentControlId, coObject);
  return coObject;
}

async function insertTgkFreeText(contentControlId: number, coObject: CoObjectItem) {
  coObject.id = await freeTextInsert(mapFreeTextConfigToFreeText(coObject));
  await awaitAndGuardTitleAndTag(contentControlId, coObject);
  return coObject;
}

async function insertTgkText(contentControlId: number, coObject: CoObjectItem, userId: string) {
  const tgkTextElaborationResponse = await startElaborationForTgkTextInsert(
    mapCoObjectToTgkTextElaborationRequest(coObject, userId)
  );

  coObject.id = tgkTextElaborationResponse.id;
  coObject.source = {
    ...coObject.source,
    process: tgkTextElaborationResponse.process,
  } as TgkTextSource;
  await awaitAndGuardTitleAndTag(contentControlId, coObject);
  return coObject;
}

async function createOrLinkDocPart(
  coObject: CoObjectItem,
  inheritableParameters?: DimensionParameter[],
  documentInsertionPoint?: DocumentInsertionPoint,
  language?: string,
  linkDetail?: { partDocumentId: string } | { documentDriveItemId: string }
) {
  let source: DocumentPartSource = coObject.source as DocumentPartSource;
  let operation: "link" | "scratch" | "nontagetik" = "scratch";

  if ((linkDetail as any)?.documentDriveItemId) {
    operation = "nontagetik";
  } else if (source.includedDocumentPartId != undefined || source.includedDocumentPartId != null) {
    operation = "link";
  }

  let itemResult = await insertDocPart(
    coObject,
    operation,
    inheritableParameters,
    language,
    documentInsertionPoint,
    (linkDetail as any)?.documentDriveItemId
  );

  let message =
    operation !== "scratch"
      ? formatMessage("cof.createnewdocument.message.linksuccess")
      : formatMessage("cof.createnewdocument.message.createsuccess");
  let title =
    operation !== "scratch"
      ? formatMessage("cof.createnewdocument.link")
      : formatMessage("cof.createnewdocument.createdocumentpart");
  NotificationManager.addNotification({
    key: CREATE_DOCUMENT_PART_NOTIFICATION,
    message: message,
    title: title,
    type: "success",
    closable: true,
    removeOnClose: true,
    isTemporary: true,
  });
  return itemResult;
}

async function insertDocPart(
  coObject: CoObjectItem,
  operation: "link" | "scratch" | "nontagetik",
  inheritableParameters?: DimensionParameter[],
  language?: string,
  documentInsertionPoint?: DocumentInsertionPoint,
  driveItemId?: string
) {
  let source: DocumentPartSource = coObject.source as DocumentPartSource;
  const objectName = normalizeDocumentName(coObject.info.objectName);

  let contentControl = await insertDocumentPart(objectName, documentInsertionPoint);
  coObject.contentControlId = contentControl.id;

  let coObjectDocumentPart: CoObjectDocumentPartInclude = mapDocumentPartConfigToDocumentPart(
    coObject,
    inheritableParameters,
    language
  );
  if (operation === "link") {
    coObjectDocumentPart = await documentPartLink(coObjectDocumentPart, documentInsertionPoint);
    coObject.id = coObjectDocumentPart.id;
    coObject.info.objectName = coObjectDocumentPart.name;
    coObject.info.info = coObjectDocumentPart.desc;
  } else if (operation === "scratch") {
    coObjectDocumentPart = await documentPartInsert(coObjectDocumentPart, documentInsertionPoint);
    coObject.id = coObjectDocumentPart.id;
    source.includedDocumentPartId = coObjectDocumentPart.includedDocumentPartId;
  } else {
    coObjectDocumentPart = await documentPartNonTagetik(coObjectDocumentPart, driveItemId, documentInsertionPoint);
    coObject.id = coObjectDocumentPart.id;
    source.includedDocumentPartId = coObjectDocumentPart.includedDocumentPartId;
  }

  coObject.info.objectName = normalizeDocumentName(coObject.info.objectName);
  addTitleAndTag(contentControl.id, coObject);
  return coObject;
}
